import React, { useEffect, useRef, useState, memo } from "react";
import {
  ConversationAgent,
  BrowserAudioInterface,
  createConversation,
  InteractionConfig,
  InteractionType,
} from "sarvam-conv-ai-sdk/browser";
import { Phone, PhoneOff, Mic, Headphones, Minimize2 } from "lucide-react";

// Icon component - moved outside to prevent re-creation on every render
const IconDisplay = memo(
  ({
    isActive,
    logoUrl,
    logoAlt,
    buttonColor,
  }: {
    isActive: boolean;
    logoUrl?: string;
    logoAlt?: string;
    buttonColor: string;
  }) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={logoAlt}
            style={{
              width: "32px",
              height: "32px",
              objectFit: "contain",
            }}
          />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isActive ? (
              <Mic size={32} color={buttonColor} />
            ) : (
              <Headphones size={32} color="#6b7280" />
            )}
          </div>
        )}
      </div>
    );
  },
);

IconDisplay.displayName = "IconDisplay";

export interface SarvamWidgetProps {
  // Required SDK configuration
  apiKey: string;
  appId: string;
  orgId: string;
  workspaceId: string;
  userId?: string;

  // Customization props
  buttonText?: string;
  title?: string;
  buttonColor?: string;
  buttonHoverColor?: string;
  textColor?: string;
  logoUrl?: string;
  logoAlt?: string;
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
  size?: "small" | "medium" | "large";
  baseUrl?: string;
  proxy?: boolean;

  // Callbacks
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: string) => void;
}

export const SarvamWidget: React.FC<SarvamWidgetProps> = ({
  apiKey,
  appId,
  orgId,
  workspaceId,
  userId = `user_${Date.now()}`,
  buttonText = "Start Voice Chat",
  title = "Sarvam Voice Assistant",
  buttonColor = "#000000",
  buttonHoverColor,
  textColor = "#ffffff",
  logoUrl,
  logoAlt = "Logo",
  position = "bottom-right",
  // @ts-ignore - baseUrl kept for API compatibility
  baseUrl: _baseUrl,
  onConnect,
  onDisconnect,
  onError,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string>("");
  const [callDuration, setCallDuration] = useState(0);
  const agentRef = useRef<ConversationAgent | null>(null);
  const audioInterfaceRef = useRef<BrowserAudioInterface | null>(null);
  const callStartTimeRef = useRef<number | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate hover color if not provided
  const hoverColor = buttonHoverColor || adjustBrightness(buttonColor, -20);

  const startConversation = async () => {
    if (isConnecting || isConnected) return;

    setIsConnecting(true);
    setError("");

    try {
      const config: InteractionConfig = {
        user_identifier_type: "custom",
        user_identifier: userId,
        org_id: orgId,
        workspace_id: workspaceId,
        app_id: appId,
        interaction_type: InteractionType.CALL,
        input_sample_rate: 16000,
        output_sample_rate: 22050,
      };

      const audioInterface = new BrowserAudioInterface(16000);
      audioInterfaceRef.current = audioInterface;

      const agent = await createConversation(config, apiKey, {
        platform: "browser",
        audioInterface,
        eventCallback: async (event: any) => {
          console.log("Event:", event);
          return Promise.resolve();
        },
        audioCallback: async (_msg: any) => {
          return Promise.resolve();
        },
        textCallback: async (msg: any) => {
          console.log("Text message:", msg);
          return Promise.resolve();
        },
        transcriptCallback: async (msg: any) => {
          console.log("Transcript:", JSON.stringify(msg, null, 2));
          const text = msg?.content || msg?.text || msg?.transcript;
          console.log("Extracted text:", text);
          if (text && text.startsWith("redirect::/")) {
            const path = text.replace("redirect::", "").replace(/^\/+/, "/");
            console.log("Dispatching sarvam:redirect event with path:", path);
            window.dispatchEvent(
              new CustomEvent("sarvam:redirect", { detail: { path } }),
            );
          }
          return Promise.resolve();
        },
      });

      agentRef.current = agent;

      const connected = await agentRef.current?.waitForConnect(5.0);

      if (connected) {
        setIsConnected(true);
        setIsConnecting(false);
        callStartTimeRef.current = Date.now();
        setCallDuration(0);
        onConnect?.();

        // Wait for disconnection
        agent.waitForDisconnect().then(() => {
          setIsConnected(false);
          setCallDuration(0);
          callStartTimeRef.current = null;
          onDisconnect?.();
        });
      } else {
        setIsConnecting(false);
        const errorMsg = "Failed to connect. Please check your credentials.";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    } catch (error) {
      setIsConnecting(false);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      setError(errorMessage);
      onError?.(errorMessage);
      console.error("Error starting conversation:", error);
    }
  };

  const stopConversation = async () => {
    if (agentRef.current) {
      try {
        await agentRef.current.stop();
        agentRef.current = null;
        setIsConnected(false);
        setCallDuration(0);
        callStartTimeRef.current = null;
        onDisconnect?.();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setError(errorMessage);
        onError?.(errorMessage);
        console.error("Error stopping conversation:", error);
      }
    }
  };

  // Call duration timer
  useEffect(() => {
    if (isConnected && callStartTimeRef.current) {
      intervalRef.current = setInterval(() => {
        if (callStartTimeRef.current) {
          const elapsed = Math.floor(
            (Date.now() - callStartTimeRef.current) / 1000,
          );
          setCallDuration(elapsed);
        }
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isConnected]);

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (agentRef.current) {
        agentRef.current.stop().catch(console.error);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleClick = () => {
    if (isConnected) {
      stopConversation();
    } else {
      startConversation();
    }
  };

  // Position styles
  const positionStyles = {
    "bottom-right": { bottom: "20px", right: "20px" },
    "bottom-left": { bottom: "20px", left: "20px" },
    "top-right": { top: "20px", right: "20px" },
    "top-left": { top: "20px", left: "20px" },
  };

  const currentPosition = positionStyles[position];

  // Format call duration as MM:SS
  const formatCallDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const fontStack =
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

  const fontSmoothing = {
    fontFamily: fontStack,
    WebkitFontSmoothing: "antialiased" as const,
    MozOsxFontSmoothing: "grayscale" as const,
    textRendering: "optimizeLegibility" as const,
  };

  return (
    <>
      <div
        style={{
          position: "fixed",
          ...currentPosition,
          zIndex: 9999,
        }}
      >
        {/* Collapsed FAB */}
        {!isExpanded && (
          <button
            onClick={() => setIsExpanded(true)}
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "50%",
              backgroundColor: buttonColor,
              color: textColor,
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 20px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "transform 0.2s ease, box-shadow 0.2s ease",
              animation: isConnected
                ? "sarvam-fab-pulse 2s ease-in-out infinite"
                : "none",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "scale(1.08)";
              e.currentTarget.style.boxShadow =
                "0 6px 24px rgba(0, 0, 0, 0.25), 0 2px 8px rgba(0, 0, 0, 0.1)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.boxShadow =
                "0 4px 20px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.1)";
            }}
          >
            {isConnected ? (
              <Mic size={24} color={textColor} />
            ) : logoUrl ? (
              <img
                src={logoUrl}
                alt={logoAlt}
                style={{ width: "28px", height: "28px", objectFit: "contain" }}
              />
            ) : (
              <Headphones size={24} color={textColor} />
            )}
          </button>
        )}

        {/* Expanded card */}
        {isExpanded && (
          <div
            style={{
              position: "relative",
              minWidth: "250px",
              maxWidth: "500px",
              backgroundColor: "#ffffff",
              borderRadius: "24px",
              boxShadow:
                "0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)",
              padding: "16px",
              ...fontSmoothing,
              animation: "sarvam-card-in 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            {/* Minimize button — top-right edge */}
            <button
              onClick={() => setIsExpanded(false)}
              style={{
                position: "absolute",
                top: "-4px",
                right: "-4px",
                width: "24px",
                height: "24px",
                borderRadius: "50%",
                border: "1px solid #e5e7eb",
                background: "#ffffff",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                transition: "background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease",
                padding: 0,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "#f3f4f6";
                e.currentTarget.style.color = "#4b5563";
                e.currentTarget.style.boxShadow = "0 2px 8px rgba(0, 0, 0, 0.15)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "#ffffff";
                e.currentTarget.style.color = "#9ca3af";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(0, 0, 0, 0.1)";
              }}
            >
              <Minimize2 size={12} />
            </button>

            {/* Top section: Icon and Title */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                marginBottom: isConnected ? "16px" : "12px",
              }}
            >
              <div style={{ flexShrink: 0 }}>
                <IconDisplay
                  isActive={isConnected}
                  logoUrl={logoUrl}
                  logoAlt={logoAlt}
                  buttonColor={buttonColor}
                />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    color: "#111827",
                    fontWeight: "500",
                    fontSize: "16px",
                    lineHeight: "1.2",
                    marginBottom: isConnected ? "4px" : "0",
                  }}
                >
                  {title}
                </div>
                {isConnected && (
                  <div
                    style={{
                      color: "#6b7280",
                      fontSize: "12px",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: "400",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {formatCallDuration(callDuration)}
                  </div>
                )}
              </div>
            </div>

            {/* Call button */}
            <button
              onClick={handleClick}
              disabled={isConnecting}
              style={{
                width: "100%",
                backgroundColor: buttonColor,
                color: textColor,
                padding: "10px 25px",
                borderRadius: "9999px",
                fontWeight: "500",
                fontSize: "14px",
                border: "none",
                cursor: isConnecting ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "all 0.2s ease",
                ...fontSmoothing,
                opacity: isConnecting ? 0.5 : 1,
              }}
              onMouseOver={(e) => {
                if (!isConnecting) {
                  e.currentTarget.style.backgroundColor = hoverColor;
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = buttonColor;
              }}
            >
              {isConnected ? (
                <>
                  <PhoneOff size={14} />
                  <span>End Call</span>
                </>
              ) : (
                <>
                  <Phone size={14} />
                  <span>{isConnecting ? "Connecting..." : buttonText}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div
          style={{
            position: "fixed",
            ...(position === "bottom-right"
              ? { bottom: "200px", right: "20px" }
              : position === "bottom-left"
                ? { bottom: "200px", left: "20px" }
                : position === "top-right"
                  ? { top: "200px", right: "20px" }
                  : { top: "200px", left: "20px" }),
            zIndex: 10000,
            padding: "14px 18px",
            backgroundColor: "#fff",
            color: "#dc2626",
            borderRadius: "12px",
            fontSize: "13px",
            maxWidth: "500px",
            boxShadow:
              "0 4px 16px rgba(220, 38, 38, 0.2), 0 0 0 1px rgba(220, 38, 38, 0.1)",
            ...fontSmoothing,
            border: "1px solid rgba(220, 38, 38, 0.15)",
            animation: "sarvam-card-in 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            lineHeight: "1.5",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}
          >
            <span style={{ fontSize: "16px", lineHeight: "1" }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  display: "block",
                  marginBottom: "4px",
                  fontSize: "13px",
                  fontWeight: "500",
                }}
              >
                Error
              </div>
              <span style={{ fontSize: "12px", opacity: 0.9 }}>{error}</span>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes sarvam-fab-pulse {
          0%, 100% { box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 0 ${buttonColor}40; }
          50% { box-shadow: 0 4px 20px rgba(0,0,0,0.2), 0 0 0 8px ${buttonColor}00; }
        }
        @keyframes sarvam-card-in {
          from { opacity: 0; transform: scale(0.92) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
};

// Helper function to adjust color brightness
function adjustBrightness(color: string, percent: number): string {
  const num = parseInt(color.replace("#", ""), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, Math.max(0, (num >> 16) + amt));
  const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + amt));
  const B = Math.min(255, Math.max(0, (num & 0x0000ff) + amt));
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}
